import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    InputAdornment,
    keyframes,
    Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomButton from "../../components/common/CustomButton";
import { userApi } from "../../api/userApi";
import { SetupRecruiterAccountSchema } from "../../components/validator/Recruiter/SetupAccount";
import CMNB from "../../assets/images/merubot_logo.png";

const GeneratePasswordPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [loading, setLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({ first_name: "", last_name: "", password: "", confirm_password: "" });
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "info",
    });

    const showSnackbar = (message, type = "info") => setSnackbar({ open: true, message, type });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    // Validate token and link on mount
    useEffect(() => {
        if (!token) {
            navigate("/restriction", { state: { reason: "expired_setup_account_link" } });
            return;
        }

        const validateToken = async () => {
            try {
                const response = await userApi.validatePasswordGenerateLink(token);
                const { status, message } = response || {};
                if (status === 200) {
                    setIsValidating(false);
                } else {
                    showSnackbar(message || "Invite link expired", "error");
                    setTimeout(() => {
                        navigate("/restriction", { state: { reason: "expired_setup_account_link" } });
                    }, 1500);
                }
            } catch (error) {
                console.error(error);
                showSnackbar("Something went wrong", "error");
                setTimeout(() => {
                    navigate("/restriction", { state: { reason: "expired_setup_account_link" } });
                }, 1500);
            }
        };
        validateToken();
    }, [token, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        if (name === "password" || name === "confirm_password") {
            processedValue = value?.trim();
        }
        const updatedFormData = { ...formData, [name]: processedValue };
        setFormData(updatedFormData);

        if (!String(processedValue).trim()) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
            return;
        }

        if (name === "confirm_password") {
            if (processedValue !== updatedFormData.password) {
                setErrors((prev) => ({ ...prev, [name]: "Passwords do not match" }));
                return;
            }
        }
        try {
            SetupRecruiterAccountSchema.validateSyncAt(name, { [name]: processedValue });
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (error) {
            setErrors((prev) => ({ ...prev, [name]: error.message }));
        }

        // When password changes, re-validate confirm_password if it has a value
        if (name === "password" && updatedFormData.confirm_password) {
            if (processedValue === updatedFormData.confirm_password) {
                setErrors((prev) => ({ ...prev, confirm_password: "" }));
            } else {
                setErrors((prev) => ({ ...prev, confirm_password: "Passwords do not match" }));
            }
        }
    };

    const handleSubmit = async () => {
        if (!token) {
            showSnackbar("Invalid or expired invite link", "error");
            return;
        }

        const trimmedFirstName = formData.first_name.trim().replace(/\s+/g, " ");
        const trimmedLastName = formData.last_name.trim().replace(/\s+/g, " ");
        const trimmedPassword = formData.password.trim();
        const trimmedConfirmPassword = formData.confirm_password.trim();

        const normalizedFormData = {
            ...formData,
            first_name: trimmedFirstName,
            last_name: trimmedLastName,
            password: trimmedPassword,
            confirm_password: trimmedConfirmPassword,
        };
        setFormData(normalizedFormData);

        if (trimmedPassword !== trimmedConfirmPassword) {
            showSnackbar("❌ Passwords do not match!", "error");
            setErrors((prev) => ({ ...prev, confirm_password: "Passwords do not match" }));
            return;
        }

        try {
            SetupRecruiterAccountSchema.validateSync(normalizedFormData, { abortEarly: false });
            setErrors({});
        } catch (error) {
            const validationErrors = {};
            if (error.inner) {
                error.inner.forEach((err) => {
                    validationErrors[err.path] = err.message;
                });
            } else {
                validationErrors[error.path] = error.message;
            }
            setErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);
            const payload = {
                token: token,
                first_name: trimmedFirstName,
                last_name: trimmedLastName,
                password: trimmedPassword,
            };

            const response = await userApi.generateRecruiterPassword(payload);
            if (response.status === 200) {
                showSnackbar("Password generated successfully", "success");
                setTimeout(() => {
                    navigate("/email/redirect");
                }, 1500);
            } else {
                showSnackbar(response.message || "Unable to generate password", "error");
            }
        } catch (error) {
            console.error(error);
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  `;

    if (isValidating) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: theme.palette.background.default,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.palette.background.grad,
            }}
        >
            <CustomCard
                sx={{
                    width: "100%",
                    maxWidth: 500,
                    textAlign: "center",
                    pt: 1,
                    pb: 3,
                    pl: 3,
                    pr: 3,
                    borderRadius: 3,
                    background: theme.palette.background.paper,
                    animation: `${fadeIn} 0.4s ease`,
                    boxShadow: theme.shadows[6],
                }}
            >
                <Box
                    component="img"
                    src={CMNB}
                    alt="Logo"
                    sx={{
                        height: 60,
                        objectFit: "contain",
                        mx: "auto",
                        display: "block",
                    }}
                />

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        pb: 0.3,
                    }}
                >
                    Setup Your Account
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        mb: 1,
                        color: theme.palette.text.secondary,
                        fontSize: "0.85rem",
                    }}
                >
                    Setup your account with strong credentials.
                </Typography>
                <Divider />

                <CustomInput
                    label="First Name"
                    name="first_name"
                    size="small"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                />

                <CustomInput
                    label="Last Name"
                    name="last_name"
                    size="small"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                />

                <CustomInput
                    label="New Password"
                    name="password"
                    size="small"
                    tooltip="Password must be at least 13 characters long and contain at least one uppercase letter, one lowercase letter, one special character and one number."
                    required
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setShowPassword(!showPassword)}
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    {showPassword ? (
                                        <VisibilityOff fontSize="small" />
                                    ) : (
                                        <Visibility fontSize="small" />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <CustomInput
                    label="Confirm Password"
                    name="confirm_password"
                    size="small"
                    required
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    {showConfirmPassword ? (
                                        <VisibilityOff fontSize="small" />
                                    ) : (
                                        <Visibility fontSize="small" />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <CustomButton
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        !formData.first_name.trim() ||
                        !formData.last_name.trim() ||
                        !formData.password.trim() ||
                        !formData.confirm_password.trim() ||
                        Object.values(errors).some((v) => v)
                    }
                    sx={{
                        mt: 1,
                    }}
                >
                    {loading ? (
                        <CircularProgress size={22} color="inherit" />
                    ) : (
                        "Setup Account"
                    )}
                </CustomButton>
            </CustomCard>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={closeSnackbar}
            />
        </Box>
    );
};

export default GeneratePasswordPage;